<template>
  <v-container fluid class="px-10">
    <v-row class="ma-0">
      <v-col cols="12">
        <h3 class="primary--text font-weight-bold pt-5 pl-2">
          <v-icon color="primary" large left>call_merge</v-icon>
          {{ $t('settings/settings:duplicate.overview.title') }}
        </h3>

        <v-card class="pa-5">
          <v-tabs v-model="currentTab" ref="tabs">
            <v-tab to="#building">
              <v-icon class="mr-1">apartment</v-icon>
              <span class="mr-1">{{ $t('common:building_plural') }}</span>
              <span v-if="buildingDuplicateCount">({{ buildingDuplicateCount }})</span>
            </v-tab>
            <v-tab to="#facility">
              <v-icon class="mr-1">device_hub</v-icon>
              <span class="mr-1">{{ $t('common:facility_plural') }}</span>
              <span v-if="facilityDuplicateCount">({{ facilityDuplicateCount }})</span>
            </v-tab>
            <v-tab to="#users">
              <v-icon class="mr-1">person</v-icon>
              <span class="mr-1">{{ $t('common:user_plural') }}</span>
              <span v-if="userDuplicateCount">({{ userDuplicateCount }})</span>
            </v-tab>
            <v-tab to="#customer">
              <v-icon class="mr-1">groups</v-icon>
              <span class="mr-1">{{ $t('common:customer_plural') }}</span>
              <span v-if="userDuplicateCount">({{ customerDuplicateCount }})</span>
            </v-tab>
          </v-tabs>

          <v-divider></v-divider>
          <div class="mb-5"></div>

          <v-tabs-items v-model="currentTab">
            <v-tab-item value="building">
              <TwoButtonDialog
                  ref="readBuildingDuplicates"
                  :btn-text="$t('settings/settings:duplicate.overview.dialog.title')"
                  color="primary"
                  :dialog-title="$t('settings/settings:duplicate.overview.dialog.title')"
                  :dialog-text="$t('settings/settings:duplicate.overview.dialog.text')"
                  :button1-text="$t('buttons:yes')"
                  :button2-text="$t('buttons:cancel')"
                  @clickedButton1="getBuildingDuplicates"
                  @clickedButton2="$refs.readBuildingDuplicates.showDialog = false"
              />

              <SpecificDuplicateOverview
                  v-show="buildingDuplicateOverview"
                  ref="buildingDuplicateOverview"
                  :headers="buildingTableHeaders"
                  group-object-field="buildings"
                  group-object-field-card-title="name"
                  group-object-field-card-subtitle="name"
                  @showObject="openBuilding"
                  @showPreview="showBuildingPreview"
                  @updateDuplicateLength="buildingDuplicateCount = $event"
              />

              <BuildingMerging
                  ref="buildingMerge"
                  @finishedMerge="removeBuildingGroup"
              />
            </v-tab-item>
            <v-tab-item value="facility">
              <TwoButtonDialog
                  ref="readFacilityDuplicates"
                  :btn-text="$t('settings/settings:duplicate.overview.dialog.title')"
                  color="primary"
                  :dialog-title="$t('settings/settings:duplicate.overview.dialog.title')"
                  :dialog-text="$t('settings/settings:duplicate.overview.dialog.text')"
                  :button1-text="$t('buttons:yes')"
                  :button2-text="$t('buttons:cancel')"
                  @clickedButton1="getFacilityDuplicates"
                  @clickedButton2="$refs.readFacilityDuplicates.showDialog = false"
              />

              <SpecificDuplicateOverview
                  v-show="facilityDuplicateOverview"
                  ref="facilityDuplicateOverview"
                  :headers="facilityTableHeaders"
                  group-object-field="facilities"
                  group-object-field-card-title="name"
                  group-object-field-card-subtitle="name"
                  @showObject="openFacility"
                  @showPreview="showFacilityPreview"
                  @updateDuplicateLength="buildingDuplicateCount = $event"
              />

              <FacilityMerging
                  ref="facilityMerge"
                  @finishedMerge="removeFacilityGroup"
                  @showOverview="showFacilityOverview"
              />
            </v-tab-item>
            <v-tab-item value="users">
              <TwoButtonDialog
                  ref="readUserDuplicates"
                  :btn-text="$t('settings/settings:duplicate.overview.dialog.title')"
                  color="primary"
                  :dialog-title="$t('settings/settings:duplicate.overview.dialog.title')"
                  :dialog-text="$t('settings/settings:duplicate.overview.dialog.text')"
                  :button1-text="$t('buttons:yes')"
                  :button2-text="$t('buttons:cancel')"
                  @clickedButton1="getUserDuplicates"
                  @clickedButton2="$refs.readUserDuplicates.showDialog = false"
              />

              <SpecificDuplicateOverview
                  v-show="userDuplicateOverview"
                  ref="userDuplicateOverview"
                  :headers="userTableHeaders"
                  group-object-field="users"
                  group-object-field-card-title="username"
                  group-object-field-card-subtitle="username"
                  checkboxOnProperty="active"
                  @showObject="openUser"
                  @showPreview="showUserPreview"
                  @updateDuplicateLength="userDuplicateCount = $event"
              />

              <UsersMerging
                  ref="userMerge"
                  @finishedMerge="removeUserGroup"
                  @showOverview="showUserOverview"
              />
            </v-tab-item>
            <v-tab-item value="customer">
              <TwoButtonDialog
                  ref="readCustomerDuplicates"
                  :btn-text="$t('settings/settings:duplicate.overview.dialog.title')"
                  color="primary"
                  :dialog-title="$t('settings/settings:duplicate.overview.dialog.title')"
                  :dialog-text="$t('settings/settings:duplicate.overview.dialog.text')"
                  :button1-text="$t('buttons:yes')"
                  :button2-text="$t('buttons:cancel')"
                  @clickedButton1="getCustomerDuplicates"
                  @clickedButton2="$refs.readCustomerDuplicates.showDialog = false"
              />

              <SpecificDuplicateOverview
                  v-show="customerDuplicateOverview"
                  ref="customerDuplicateOverview"
                  :headers="customerTableHeaders"
                  group-object-field="customers"
                  group-object-field-card-title="name"
                  group-object-field-card-subtitle="name"
                  @showObject="openCustomer"
                  @showPreview="showCustomerPreview"
                  @updateDuplicateLength="customerDuplicateCount = $event"
              />

              <CustomerMerging
                  ref="customerMerge"
                  @finishedMerge="removeCustomerGroup"
                  @showOverview="showCustomerOverview"
              />
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import TwoButtonDialog from "@/modules/core/components/TwoButtonDialog/TwoButtonDialog";
import BuildingMerging from "@/modules/settings/views/settings/duplicateMerge/BuildingMerging";
import DuplicateCheckService from "@/modules/core/services/duplicate-check-service";
import Facility from "@/modules/book/data/facility";
import DataUtils from "@/modules/core/services/data-utils";
import SpecificDuplicateOverview from "@/modules/settings/views/settings/duplicateMerge/SpecificDuplicateOverview";
import Building from "@/modules/book/data/building";
import Utils from "@/modules/core/services/utils";
import FacilityMerging from "@/modules/settings/views/settings/duplicateMerge/FacilityMerging";
import Users from "@/modules/core/data/users";
import UsersMerging from "@/modules/settings/views/settings/duplicateMerge/UsersMerging";
import Customer from "@/modules/core/data/customer";
import CustomerMerging from "@/modules/settings/views/settings/duplicateMerge/CustomerMerging";
export default {
  name: "DuplicateOverview",
  components: {
    CustomerMerging,
    UsersMerging, FacilityMerging, SpecificDuplicateOverview, BuildingMerging, TwoButtonDialog},
  data() {
    return {
      currentTab: 'building',
      buildingDuplicateCount: null,
      facilityDuplicateCount: null,
      userDuplicateCount: null,
      customerDuplicateCount: null,

      buildingDuplicateOverview: false,
      facilityDuplicateOverview: false,
      userDuplicateOverview: false,
      customerDuplicateOverview: false
    }
  },
  computed: {
    buildingTableHeaders() {
      return [
        {text: this.$t('common:name'), value: 'name'},
        {text: this.$t('common:object-number'), value: 'objectNumber'},
        {text: this.$t('common:address'), value: 'address_street'},
        {text: this.$t('settings/settings:duplicate.overview.table.role-count'), value: 'roleCount'},
        {text: this.$t('settings/settings:duplicate.overview.table.facility-count'), value: 'facilityCount'},
        {text: this.$t('common:created_at'), value: 'createdAt'},
        {text: this.$t('common:last-updated-on'), value: 'updatedAt'}
      ]
    },

    facilityTableHeaders() {
      return [
        {text: this.$t('common:name'), value: 'name'},
        {text: this.$t('common:facility-number'), value: 'objectNumber'},
        {text: this.$t('common:type'), value: 'typeName'},
        {text: this.$t('common:manager'), value: 'managerName'},
        {text: this.$t('settings/settings:duplicate.overview.table.contract-count'), value: 'contractCount'},
        {text: this.$t('settings/settings:duplicate.overview.table.role-count'), value: 'roleCount'},
        {text: this.$t('settings/settings:duplicate.overview.table.document-count'), value: 'documentationCount'},
        {text: this.$t('settings/settings:duplicate.overview.table.process-count'), value: 'processJobCount'},
        {text: this.$t('common:created_at'), value: 'createdAt'},
        {text: this.$t('common:last-updated-on'), value: 'updatedAt'}
      ];
    },

    userTableHeaders() {
      return [
        {text: this.$t('settings/settings:duplicate.overview.table.username'), value: 'username'},
        {text: this.$t('common:name'), value: 'name'},
        {text: this.$t('common:email'), value: 'email'},
        {text: this.$t('settings/settings:duplicate.overview.table.user-role'), value: 'translatedBookHierarchy'},
        {text: this.$t('common:customer'), value: 'customerName'},
        {text: this.$t('common:active'), value: 'checkbox'},
        {text: this.$t('common:created_at'), value: 'createdAt'},
        {text: this.$t('common:last-updated-on'), value: 'updatedAt'}
      ]
    },

    customerTableHeaders() {
      return [
        {text: this.$t('common:name'), value: 'name'},
        {text: this.$t('common:street'), value: 'street'},
        {text: this.$t('common:zip'), value: 'zip'},
        {text: this.$t('common:city'), value: 'city'},
        {text: this.$t('settings/settings:duplicate.overview.table.contact-name'), value: 'contactName'},
        {text: this.$t('settings/settings:duplicate.overview.table.contact-mail'), value: 'contactMail'},
        {text: this.$t('settings/settings:duplicate.overview.table.contact-phone'), value: 'contactPhone'},
        {text: this.$t('settings/settings:duplicate.overview.table.vat-id'), value: 'vatId'},
        {text: this.$t('common:created_at'), value: 'createdAt'},
        {text: this.$t('common:last-updated-on'), value: 'updatedAt'}
      ]
    }
  },
  methods: {
    getBuildingDuplicates() {
      this.buildingDuplicateCount = null;

      this.showBuildingOverview();

      this.$refs.readBuildingDuplicates.showDialog = false;
      this.$refs.buildingDuplicateOverview.duplicates = [];
      this.$refs.buildingDuplicateOverview.isLoading = true;

      DuplicateCheckService.readAllDuplicatedBuildingsFromAPI(
          (res) => {
            let duplicates = [];
            res.data.forEach((buildingGroup) => {
              let groupObject = {};
              groupObject.buildings = DataUtils.parseDataToObjectArray(buildingGroup, Building);
              groupObject.buildings.forEach((building) => {
                building.merge = true;
              });
              groupObject.primaryObjectId = null;
              duplicates.push(groupObject);
            })
            this.buildingDuplicateCount = duplicates.length;
            this.$refs.buildingDuplicateOverview.duplicates = duplicates;
            this.$refs.buildingDuplicateOverview.isLoading = false;
            this.$refs.buildingDuplicateOverview.noDuplicates = duplicates.length === 0;
          },
          (error) => {
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('settings/settings:duplicate.overview.duplicates')}))
            this.$logger.error("Error at reading building duplicates: " + error);
          }
      )
    },

    openBuilding(buildingId) {
      window.open(`/${this.$store.state.manager.bookModuleUrl}/buildings/edit/${buildingId}`);
    },

    showBuildingPreview(duplicateGroup) {
      this.$refs.buildingMerge.createPreview(duplicateGroup);
    },

    showBuildingOverview() {
      this.buildingDuplicateOverview = true;
      this.$refs.buildingMerge.closeDialog();
    },

    removeBuildingGroup(idsToRemove) {
      this.showBuildingOverview();
      this.$refs.buildingDuplicateOverview.removeDuplicateGroup(idsToRemove);
    },

    getFacilityDuplicates() {
      this.facilityDuplicateCount = null;

      this.showFacilityOverview();

      this.$refs.facilityDuplicateOverview.duplicates = [];
      this.$refs.facilityDuplicateOverview.isLoading = true;

      this.$refs.readFacilityDuplicates.showDialog = false;

      DuplicateCheckService.readAllDuplicatedFacilitiesFromAPI(
          (res) => {
            let duplicates = [];
            res.data.forEach((facilityGroup) => {
              let groupObject = {};
              groupObject.facilities = DataUtils.parseDataToObjectArray(facilityGroup, Facility);
              groupObject.facilities.forEach((facility) => {
                facility.merge = true;
              });

              groupObject.primaryObjectId = null;
              duplicates.push(groupObject);
            })
            this.facilityDuplicateCount = duplicates.length;
            this.$refs.facilityDuplicateOverview.duplicates = duplicates;
            this.$refs.facilityDuplicateOverview.isLoading = false;
            this.$refs.facilityDuplicateOverview.noDuplicates = duplicates.length === 0;
          },
          (error) => {
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('settings/settings:duplicate.overview.duplicates')}))
            this.$logger.error("Error at reading facility duplicates: " + error);
          }
      )
    },

    showFacilityPreview(duplicateGroup) {
      this.$refs.facilityMerge.createPreview(duplicateGroup);
    },

    showFacilityOverview() {
      this.facilityDuplicateOverview = true;
      this.$refs.facilityMerge.closeDialog();
    },

    removeFacilityGroup(idsToRemove) {
      this.showFacilityOverview();
      this.$refs.facilityDuplicateOverview.removeDuplicateGroup(idsToRemove);
    },

    openFacility(facilityId) {
      window.open(`/${this.$store.state.manager.bookModuleUrl}/facilities/edit/${facilityId}`);
    },

    getUserDuplicates() {
      this.userDuplicateCount = null;

      this.showUserOverview();

      this.$refs.readUserDuplicates.showDialog = false;
      this.$refs.userDuplicateOverview.duplicates = [];
      this.$refs.userDuplicateOverview.isLoading = true;

      DuplicateCheckService.readAllDuplicatedUsersFromAPI(
          (res) => {
            let duplicates = [];
            res.data.forEach((userGroup) => {
              let groupObject = {};
              groupObject.users = DataUtils.parseDataToObjectArray(userGroup, Users);
              groupObject.users.forEach((user) => {
                user.translatedBookHierarchy = user.bookHierarchy ? this.$t('enums:hierarchy.' + user.bookHierarchy) : '-';
                user.merge = true;
              });
              groupObject.primaryObjectId = null;
              duplicates.push(groupObject);
            })
            this.userDuplicateCount = duplicates.length;
            this.$refs.userDuplicateOverview.duplicates = duplicates;
            this.$refs.userDuplicateOverview.isLoading = false;
            this.$refs.userDuplicateOverview.noDuplicates = duplicates.length === 0;
          },
          (error) => {
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('settings/settings:duplicate.overview.duplicates')}))
            this.$logger.error("Error at reading user duplicates: " + error);
          }
      )
    },

    openUser(userId) {
      window.open(`/${this.$store.state.manager.settingsModuleUrl}/profile/customer/users?user=${userId}`);
    },

    showUserPreview(duplicateGroup) {
      this.$refs.userMerge.createPreview(duplicateGroup);
    },

    showUserOverview() {
      this.userDuplicateOverview = true;
      this.$refs.userMerge.closeDialog();
    },

    removeUserGroup(idsToRemove) {
      this.showUserOverview();
      this.$refs.userDuplicateOverview.removeDuplicateGroup(idsToRemove);
    },

    getCustomerDuplicates() {
      this.customerDuplicateCount = null;

      this.showCustomerOverview();

      this.$refs.customerDuplicateOverview.duplicates = [];
      this.$refs.customerDuplicateOverview.isLoading = true;

      this.$refs.readCustomerDuplicates.showDialog = false;

      DuplicateCheckService.readAllDuplicatedCustomersFromAPI(
          (res) => {
            let duplicates = [];
            res.data.forEach((customerGroup) => {
              let groupObject = {};
              groupObject.customers = DataUtils.parseDataToObjectArray(customerGroup, Customer);
              groupObject.customers.forEach((customer) => {
                customer.merge = true;
              });

              groupObject.primaryObjectId = null;
              duplicates.push(groupObject);
            });

            this.customerDuplicateCount = duplicates.length;
            this.$refs.customerDuplicateOverview.duplicates = duplicates;
            this.$refs.customerDuplicateOverview.isLoading = false;
            this.$refs.customerDuplicateOverview.noDuplicates = duplicates.length === 0;
          },
          (error) => {
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('settings/settings:duplicate.overview.duplicates')}))
            this.$logger.error("Error at reading customer duplicates: " + error);
          }
      )
    },

    showCustomerPreview(duplicateGroup) {
      this.$refs.customerMerge.createPreview(duplicateGroup);
    },

    showCustomerOverview() {
      this.customerDuplicateOverview = true;
      this.$refs.customerMerge.closeDialog();
    },

    removeCustomerGroup(idsToRemove) {
      this.showCustomerOverview();
      this.$refs.customerDuplicateOverview.removeDuplicateGroup(idsToRemove)
    },

    openCustomer(customerId) {
      window.open(`/${this.$store.state.manager.settingsModuleUrl}/customer/edit/${customerId}`)
    }
  },
  watch: {
    buildingDuplicateCount() {
      this.$refs.tabs.callSlider();
    },
    facilityDuplicateCount() {
      this.$refs.tabs.callSlider();
    }
  }
}
</script>